import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const Sel = (props) => <select {...props} className={`border border-slate-200 rounded-md px-2 py-2 text-sm bg-white ${props.className || ''}`} />;
const ENTITIES = ['term', 'programme', 'course', 'section', 'registration', 'student_programme'];

const Fields = () => {
  const qc = useQueryClient();
  const { data: fields } = useQuery({ queryKey: ['acad-fields-all'], queryFn: () => api.get('/acad/custom-fields').then((r) => r.data) });
  const [f, setF] = useState({ entity: 'registration', label: '', field_type: 'text', options: '', required: false });
  const create = async () => {
    try {
      await api.post('/acad/custom-fields', { ...f, options: f.options.split(',').map((x) => x.trim()).filter(Boolean) });
      toast.success('Field added'); setF({ ...f, label: '', options: '' });
      qc.invalidateQueries({ queryKey: ['acad-fields-all'] }); qc.invalidateQueries({ queryKey: ['acad-fields'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (id) => {
    try { await api.post(`/acad/custom-fields/${id}/toggle`); qc.invalidateQueries({ queryKey: ['acad-fields-all'] }); qc.invalidateQueries({ queryKey: ['acad-fields'] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="custom-fields-manager">
      <p className="font-display font-bold mb-1">Custom fields</p>
      <p className="text-xs text-slate-500 mb-3">Add your own attributes to terms, programmes, courses, sections, registrations and student records. They appear on the matching forms automatically.</p>
      <div className="grid sm:grid-cols-5 gap-2 items-end">
        <Sel data-testid="field-entity" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })}>{ENTITIES.map((x) => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}</Sel>
        <Input data-testid="field-label" placeholder="Field label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <Sel data-testid="field-type" value={f.field_type} onChange={(e) => setF({ ...f, field_type: e.target.value })}>{['text', 'number', 'date', 'select', 'yesno'].map((x) => <option key={x}>{x}</option>)}</Sel>
        {f.field_type === 'select' && <Input data-testid="field-options" placeholder="Options, comma separated" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} />}
        <label className="text-xs text-slate-600 flex items-center gap-1"><input data-testid="field-required" type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} /> Required</label>
      </div>
      <Button data-testid="field-create" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={!f.label.trim()} onClick={create}>Add field</Button>
      <div className="mt-4">
        {(fields || []).map((d) => (
          <div key={d.id} className="border-b border-slate-50 py-1.5 flex items-center gap-2 text-sm" data-testid={`field-row-${d.id}`}>
            <span className={d.active ? '' : 'line-through text-slate-400'}><b>{d.label}</b></span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{d.entity.replace(/_/g, ' ')}</span>
            <span className="text-xs text-slate-400">{d.field_type}{d.required ? ' · required' : ''}{d.options?.length ? ` · ${d.options.join('/')}` : ''}</span>
            <button data-testid={`field-toggle-${d.id}`} onClick={() => toggle(d.id)} className="ms-auto text-xs text-orange-600 underline">{d.active ? 'Retire' : 'Restore'}</button>
          </div>
        ))}
        {(fields || []).length === 0 && <p className="text-sm text-slate-400">No custom fields yet.</p>}
      </div>
    </Card>
  );
};

const Groups = () => {
  const qc = useQueryClient();
  const { data, isError } = useQuery({ queryKey: ['acad-groups'], retry: false, queryFn: () => api.get('/acad/groups').then((r) => r.data) });
  const { data: staff } = useQuery({ queryKey: ['acad-staff'], queryFn: () => api.get('/acad/staff').then((r) => r.data).catch(() => []) });
  const [f, setF] = useState({ name: '', members: {}, ents: {} });
  if (isError) return null;
  const create = async () => {
    try {
      await api.post('/acad/groups', { name: f.name, member_user_ids: Object.keys(f.members).filter((k) => f.members[k]), entitlements: Object.keys(f.ents).filter((k) => f.ents[k]) });
      toast.success('Group created'); setF({ name: '', members: {}, ents: {} });
      qc.invalidateQueries({ queryKey: ['acad-groups'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/acad/groups/${id}`); qc.invalidateQueries({ queryKey: ['acad-groups'] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="groups-manager">
      <p className="font-display font-bold mb-1">Groups and entitlements</p>
      <p className="text-xs text-slate-500 mb-3">Grant academic permissions beyond a person's base role - for example give a senior teacher grade-approval rights.</p>
      <Input data-testid="group-name" className="w-64 mb-2" placeholder="Group name (e.g. Grade approvers)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <p className="text-xs font-semibold text-slate-500 mb-1">Entitlements</p>
      <div className="flex gap-2 flex-wrap mb-3">
        {(data?.entitlements || []).map((p) => (
          <label key={p} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${f.ents[p] ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
            <input type="checkbox" data-testid={`ent-${p}`} className="hidden" checked={!!f.ents[p]} onChange={(e) => setF({ ...f, ents: { ...f.ents, [p]: e.target.checked } })} />{p}
          </label>
        ))}
      </div>
      <p className="text-xs font-semibold text-slate-500 mb-1">Members</p>
      <div className="flex gap-2 flex-wrap mb-3 max-h-28 overflow-auto">
        {(staff || []).map((u) => (
          <label key={u.id} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${f.members[u.id] ? 'bg-orange-600 text-white border-orange-600' : 'border-slate-200 text-slate-600'}`}>
            <input type="checkbox" data-testid={`member-${u.id}`} className="hidden" checked={!!f.members[u.id]} onChange={(e) => setF({ ...f, members: { ...f.members, [u.id]: e.target.checked } })} />{u.name}
          </label>
        ))}
      </div>
      <Button data-testid="group-create" className="bg-slate-900 hover:bg-slate-800" disabled={!f.name.trim()} onClick={create}>Create group</Button>
      <div className="mt-4">
        {(data?.groups || []).map((g) => (
          <div key={g.id} className="border-b border-slate-50 py-2 flex items-start justify-between gap-2 text-sm" data-testid={`group-row-${g.id}`}>
            <div>
              <b>{g.name}</b> <span className="text-xs text-slate-400">{g.member_user_ids.length} member(s)</span>
              <p className="text-xs text-slate-500">{g.entitlements.join(' · ') || 'No entitlements'}</p>
            </div>
            <button data-testid={`group-delete-${g.id}`} onClick={() => del(g.id)} className="text-xs text-rose-500 underline">Remove</button>
          </div>
        ))}
        {(data?.groups || []).length === 0 && <p className="text-sm text-slate-400">No groups yet.</p>}
      </div>
    </Card>
  );
};

export const CustomizationTab = () => (
  <div className="space-y-4">
    <Fields />
    <Groups />
  </div>
);
